
import { AnimatePresence, motion } from "framer-motion";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { RxCross2 } from "react-icons/rx";
import Helpers from "../../utils/helpers";
import { IoIosSearch } from "react-icons/io";

const OSearch = forwardRef(
  ({ placeholders, onChange, className }, ref) => {
    const intervalRef = useRef(null);
    const handleVisibilityChange = () => {
      if (
        Helpers?.andOperator(
          document.visibilityState !== "visible",
          intervalRef.current
        )
      ) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    useEffect(() => {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      };
    }, [placeholders]);

    const canvasRef = useRef(null);
    const newDataRef = useRef([]);
    const inputRef = useRef(null);
    const [value, setValue] = useState("");
    const [animating, setAnimating] = useState(false);

    const draw = useCallback(() => {
      if (!inputRef.current) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const scale = window.devicePixelRatio || 2;
      const container = canvas.parentElement;
      const containerWidth = container.clientWidth * 1.7;
      canvas.width = containerWidth * scale;
      canvas.height = 800;
      ctx.clearRect(0, 0, 800, 800);
      const computedStyles = getComputedStyle(inputRef.current);

      const fontSize = parseFloat(computedStyles.getPropertyValue("font-size"));
      ctx.font = `${fontSize * 2}px ${computedStyles.fontFamily}`;
      ctx.fillStyle = "#FFF";
      ctx.fillText(value, 16, 40);

      const imageData = ctx.getImageData(0, 0, 800, 800);
      const pixelData = imageData.data;
      const newData = [];

      for (let t = 0; t < 800; t++) {
        let i = 4 * t * 800;
        for (let n = 0; n < 800; n++) {
          let e = i + 4 * n;
          if (
            Helpers.andOperator(
              Helpers.andOperator(pixelData[e] !== 0, pixelData[e + 1] !== 0),
              pixelData[e + 2] !== 0
            )
          ) {
            newData.push({
              x: n,
              y: t,
              color: [
                pixelData[e],
                pixelData[e + 1],
                pixelData[e + 2],
                pixelData[e + 3],
              ],
            });
          }
        }
      }

      newDataRef.current = newData.map(({ x, y, color }) => ({
        x,
        y,
        r: 1,
        color: `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`,
      }));
    }, [value]);

    useEffect(() => {
      draw();
    }, [value, draw]);

    const updateCurrent = (current, pos) => {
      if (!current) return null;

      return current.x < pos
        ? current
        : Helpers.ternaryCondition(current.r <= 0, null, {
            ...current,
            x: current.x + Helpers.ternaryCondition(Math.random() > 0.5, 1, -1),
            y: current.y + Helpers.ternaryCondition(Math.random() > 0.5, 1, -1),
            r: current.r - 0.05 * Math.random(),
          });
    };

    const drawCurrent = (ctx, current, pos) => {
      if (!ctx || !current || current.x <= pos) return;

      ctx.beginPath();
      ctx.rect(current.x, current.y, current.r, current.r);
      ctx.fillStyle = current.color;
      ctx.strokeStyle = current.color;
      ctx.stroke();
    };

    const clearCanvas = (ctx, pos) => {
      ctx && ctx.clearRect(pos, 0, 800, 800);
    };

    const animate = (start) => {
      const animateFrame = (pos = 0) => {
        requestAnimationFrame(() => {
          const ctx = canvasRef.current?.getContext("2d");
          const currentData = Helpers.orOperator(newDataRef?.current, []);
          const newArr = [];

          for (const current of currentData) {
            const updated = updateCurrent(current, pos);
            updated && newArr.push(updated);
          }

          newDataRef.current = newArr;
          clearCanvas(ctx, pos);

          for (const item of newArr) {
            drawCurrent(ctx, item, pos);
          }

          Helpers.ternaryCondition(
            newArr.length > 0,
            () => animateFrame(pos - 8),
            () => {
              setValue("");
              setAnimating(false);
            }
          )();
        });
      };

      animateFrame(start);
    };

    const vanishAndSubmit = () => {
      setAnimating(true);
      draw();
      const value = Helpers.orOperator(inputRef.current?.value, "");
      if (Helpers.andOperator(value, inputRef.current)) {
        const maxX = newDataRef.current.reduce(
          (prev, current) =>
            Helpers.ternaryCondition(current.x > prev, current.x, prev),
          0
        );
        animate(maxX);
      }
    };

    const onClose = () => {
      if (value) {
        let e = { target: { value: "" } };
        onChange && onChange(e);
        vanishAndSubmit();
      }
    };

    useImperativeHandle(ref, () => ({
      onClose,
    }));

    return (
      <div
        className={Helpers.cn(
          "w-full relative max-w-xl bg-white dark:bg-zinc-800 h-12  overflow-hidden shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),_0px_1px_0px_0px_rgba(25,28,33,0.02),_0px_0px_0px_1px_rgba(25,28,33,0.08)] transition duration-200",
          Helpers.andOperator(value, "bg-gray-50"),
          className
        )}
      >
        <canvas
          className={Helpers.cn(
            "absolute pointer-events-none  text-base transform scale-50 top-[10%] left-2 origin-top-left filter invert dark:invert-0 ",
            Helpers.ternaryCondition(!animating, "opacity-0", "opacity-100")
          )}
          ref={canvasRef}
        />
        <input
          onChange={(e) => {
            if (!animating) {
              setValue(e.target.value);
              onChange && onChange(e);
            }
          }}
          ref={inputRef}
          value={value}
          type="text"
          className={Helpers.cn(
            "w-full relative text-sm sm:text-base z-50 border-none dark:text-white !top-[-1px] bg-transparent text-black h-full rounded-full focus:outline-none focus:ring-0 pl-4 sm:pl-4 pr-8",
            Helpers.andOperator(
              animating,
              "text-transparent dark:text-transparent"
            )
          )}
        />
        {Helpers.ternaryCondition(
          value,
          <button
            type="button"
            onClick={() => {
              onClose();
            }}
            className="absolute right-1 dark:text-white top-1/2 z-50 -translate-y-1/2 h-8 w-8 rounded-full transition duration-200 flex items-center justify-center"
          >
            <RxCross2 />
          </button>,
          <div className="absolute right-1 dark:text-white top-1/2 z-50 -translate-y-1/2 h-8 w-8 rounded-full transition duration-200 flex items-center justify-center">
            <IoIosSearch />
          </div>
        )}
        <div className="absolute inset-0 flex items-center rounded-full pointer-events-none">
          <AnimatePresence mode="wait">
            {Helpers.andOperator(
              !value,
              <motion.span className="dark:text-zinc-500 !text-sm sm:text-base font-normal text-neutral-500 pl-2 sm:pl-4 text-left w-[calc(100%-2rem)] truncate">
                {placeholders}
              </motion.span>
            )}
          </AnimatePresence>
          
        </div>
      </div>
    );
  }
);

OSearch.displayName = "PlaceholdersAndVanishInput";
export default OSearch;
